import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Send, Check, MessageSquare, Clock, Zap } from "lucide-react";
import ConsentModal from "../components/ConsentModal";
import { useHoneypot } from "../components/Honeypot";
import useSeo, { buildBreadcrumb } from "../lib/useSeo";

export default function Contacto() {
  useSeo({
    title: "Contacto · DoSoft Software Factory Chile",
    description: "Habla con DoSoft sobre tu proyecto de software a medida. Equipo experto en transformación digital, MVPs y desarrollo de aplicaciones para empresas en Chile.",
    canonical: "https://dosoft.cl/contacto",
    jsonLd: [
      buildBreadcrumb([
        { name: "Inicio", url: "/" },
        { name: "Contacto", url: "/contacto" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        url: "https://dosoft.cl/contacto",
        inLanguage: "es-CL",
        mainEntity: { "@id": "https://dosoft.cl/#org" },
      },
    ],
  });
  const nav = useNavigate();
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentAt, setConsentAt] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", industry: "", message: "" });
  const [industryOther, setIndustryOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, HoneypotField] = useHoneypot();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const industryValue =
        form.industry === "Otro (Especificar)" && industryOther.trim()
          ? `Otro: ${industryOther.trim()}`
          : form.industry;
      await api.post("/leads", {
        ...form,
        industry: industryValue,
        source: "contact_form",
        consent_given: true,
        consent_at: consentAt,
        _hp: hp,
      });
      setDone(true);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      toast.success("Mensaje enviado. Nos contactaremos contigo pronto.");
      setForm({ name: "", email: "", phone: "", company: "", industry: "", message: "" });
      setIndustryOther("");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contacto-page" className="py-16 md:py-24">
      {!consentGiven && (
        <ConsentModal
          onAccept={() => {
            setConsentAt(new Date().toISOString());
            setConsentGiven(true);
          }}
          onReject={() => nav("/")}
          dataDescription="Nombre, email, teléfono, empresa, industria y mensaje que nos compartas en este formulario de contacto."
        />
      )}      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">Contacto</div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter text-ash-100 max-w-3xl leading-[0.95] mb-16">
          Hablemos de tu<br/>próximo sistema.
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-ink-500 border border-ink-500">
          {/* Info */}
          <div className="bg-ink-900 lg:col-span-5 p-10 md:p-14">
            <div className="inline-flex items-center gap-3 mb-6 border border-ink-500 px-3 py-2">
              <MessageSquare size={14} strokeWidth={1.5} className="text-brand" />
              <span className="text-[10px] uppercase tracking-widest text-ash-300 font-mono">
                Conexión 100% por la plataforma
              </span>
            </div>
            <h2 className="font-display font-bold text-2xl text-ash-100 mb-4">
              Hablemos por acá.
            </h2>
            <p className="text-ash-300 leading-relaxed mb-10">
              Toda la conexión con DoSoft se hace desde la plataforma. Déjanos un
              mensaje aquí o usa el cotizador y nuestro equipo te contactará para
              conversar los detalles.
            </p>

            <div className="space-y-5">
              <FlowItem
                icon={Zap}
                title="01 / Mensaje"
                desc="Cuéntanos brevemente qué necesitas (este formulario o el cotizador)."
              />
              <FlowItem
                icon={Clock}
                title="02 / Revisión"
                desc="Nuestro equipo revisa tu solicitud y la deriva al área apropiada."
              />
              <FlowItem
                icon={MessageSquare}
                title="03 / Discovery"
                desc="Agendamos una reunión para entender tu problema en detalle."
              />
            </div>
          </div>

          {/* Form */}
          <div className="bg-ink-800 lg:col-span-7 p-10 md:p-14">
            {done ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-2 border-brand mx-auto flex items-center justify-center mb-6">
                  <Check size={28} className="text-brand" />
                </div>
                <h3 className="font-display font-bold text-2xl text-ash-100 mb-3">Mensaje recibido</h3>
                <p className="text-ash-300 mb-8">Revisaremos tu mensaje y nos contactaremos contigo a la brevedad.</p>
                <button onClick={() => setDone(false)} className="border border-ink-500 hover:border-brand px-6 py-3 text-xs uppercase tracking-widest text-ash-100">
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <HoneypotField />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Nombre *">
                    <input required data-testid="contact-input-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </Field>
                  <Field label="Email *">
                    <input required type="email" data-testid="contact-input-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </Field>
                  <Field label="Teléfono">
                    <input data-testid="contact-input-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </Field>
                  <Field label="Empresa">
                    <input data-testid="contact-input-company" className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  </Field>
                </div>
                <Field label="Industria">
                  <select data-testid="contact-input-industry" className={inputCls} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
                    <option value="">Selecciona…</option>
                    {["Minería", "Servicios", "Salud", "Educación", "Particular", "Otro (Especificar)"].map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </Field>
                {form.industry === "Otro (Especificar)" && (
                  <Field label="¿Cuál industria?">
                    <input
                      data-testid="contact-input-industry-other"
                      className={inputCls}
                      placeholder="Especifica tu industria"
                      value={industryOther}
                      onChange={(e) => setIndustryOther(e.target.value)}
                    />
                  </Field>
                )}
                <Field label="Mensaje *">
                  <textarea required rows={5} data-testid="contact-input-message" className={inputCls} placeholder="Cuéntanos en qué podemos ayudarte…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-8 py-4 uppercase tracking-wider text-sm disabled:opacity-50"
                >
                  {submitting ? "Enviando…" : "Enviar mensaje"} <Send size={16} strokeWidth={2} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand focus:ring-1 focus:ring-brand outline-none px-4 py-3 text-base font-body placeholder:text-ash-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ash-300 uppercase tracking-widest mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function FlowItem({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 border border-ink-500 flex items-center justify-center shrink-0">
        <Icon size={16} strokeWidth={1.5} className="text-brand" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-brand mb-1 font-mono">{title}</div>
        <div className="text-sm text-ash-100 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
